import React, { useEffect, useMemo, useRef, useState } from "react";
import { Table2 } from "../../components/Table2/Table2";
import { DynamicTabb } from "../../components/DynamicTab/DynamicTab";
import LoadPanel from "devextreme-react/load-panel";

import QuerySetup from "./components/QuerySetup";
import FilterBuilder from "./components/FilterBuilder";
import SaveQueryPanel from "./components/SaveQueryPanel";
import ActionPanel from "./components/ActionPanel";
import {
  getQueryTypes,
  getFields,
  executeQuery,
  previewQuery,
  saveQuery,
  exportQuery,
  getSavedQueries,
} from "./queryBuilderApi";
import "./QueryBuilder.css";
import SavedQueriesGrid from "./components/SavedQueriesGrid";
import QueryResultsGrid from "./components/QueryResultsGrid";
import { formatDate } from "../Dateapi/dateFormatter";
import { getExecutePopupHtml } from "./components/queryPopupTemplate";

// Simple filter generator
const newFilter = () => ({
  id: crypto.randomUUID(),
  logic: "AND",
  field: "",
  operator: "equals",
  value: "",
  value2: "",
  values: [],
});

export default function QueryRunnerPage() {
  const tableRef = useRef(null);

  // Auth
  const domain = localStorage.getItem("pathURL");
  const jwtToken = localStorage.getItem("authToken");
  const gUserId = localStorage.getItem("gUserId");

  // State
  const [queryTypes, setQueryTypes] = useState([]);
  const [queryType, setQueryType] = useState("");

  const [fields, setFields] = useState([]);
  const [filters, setFilters] = useState([newFilter()]);
  const [selectedColumns, setSelectedColumns] = useState([]);

  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [includeTotalCount, setIncludeTotalCount] = useState(true);
  const [queryName, setQueryName] = useState("");
  const [queryDescription, setQueryDescription] = useState("");
  const [visibility, setVisibility] = useState("Private");
  const [shareType, setShareType] = useState("Role");
  const [shareValue, setShareValue] = useState("");
  const [shareCanEdit, setShareCanEdit] = useState(false);
  const [savedQueries, setSavedQueries] = useState([]);
  const [exportFormat, setExportFormat] = useState("EXCEL");
  const [previewColumns, setPreviewColumns] = useState([]);

  const getFriendlyColumnName = (fieldName) => {
    const field = fields.find((f) => f.fieldName === fieldName);
    return field?.friendlyLabel || fieldName;
  };

  useEffect(() => {
    loadQueryTypes();
    loadSavedQueries();
  }, []);

  useEffect(() => {
    if (queryType) loadFields();
  }, [queryType]);

  const isHiddenColumn = (fieldName) => queryType === "Activities" && fieldName?.toLowerCase() === "grecordid";

  const getVisibleSelectedColumns = (cols = selectedColumns) => (cols || []).filter((col) => !isHiddenColumn(col));

  const loadQueryTypes = async () => {
    try {
      setLoading(true);
      const data = await getQueryTypes(domain, jwtToken, gUserId);
      setQueryTypes(data || []);

      if (data?.length > 0) {
        setQueryType(data[0].id);
      }
    } catch (e) {
      console.error("Error loading query types", e);
    } finally {
      setLoading(false);
    }
  };
  const buildPayloadFromValues = ({
    queryTypeValue,
    pageSizeValue,
    includeTotalCountValue,
    selectedColumnsValue,
    sortFieldValue,
    sortDirectionValue,
    filtersValue,
  }) => ({
    queryType: queryTypeValue,
    page: 1,
    pageSize: pageSizeValue,
    includeTotalCount: includeTotalCountValue,
    selectedColumns: getVisibleSelectedColumns(selectedColumnsValue),
    sort: sortFieldValue ? [{ field: sortFieldValue, direction: sortDirectionValue }] : [],
    filters: filtersValue
      .filter((f) => f.field && f.operator && (f.value !== "" || f.operator === "isBlank"))
      .map((f, index) => ({
        logic: index === 0 ? "AND" : f.logic,
        field: f.field,
        operator: f.operator,
        value: f.value,
        value2: f.value2 || null,
      })),
    parameters: {},
  });

  const loadSavedQuery = async (query) => {
    try {
      setLoading(true);

      const parsedColumns = JSON.parse(query.selectedColumnsJson || "[]");
      const selectedColumnsValue = getVisibleSelectedColumns(Array.isArray(parsedColumns) ? parsedColumns : []);

      const parsedSort = JSON.parse(query.sortJson || "[]");
      const sortFieldValue = Array.isArray(parsedSort) && parsedSort.length > 0 ? parsedSort[0].field || "" : "";

      const sortDirectionValue =
        Array.isArray(parsedSort) && parsedSort.length > 0 ? parsedSort[0].direction || "asc" : "asc";

      const parsedFilters = JSON.parse(query.filterJson || "[]");

      const filtersValue =
        Array.isArray(parsedFilters) && parsedFilters.length > 0
          ? parsedFilters.map((f) => ({
              id: crypto.randomUUID(),
              logic: f.logic || "AND",
              field: f.field || "",
              operator: f.operator || "equals",
              value: f.value || "",
              value2: f.value2 || "",
              values: Array.isArray(f.value) ? f.value : [],
            }))
          : [newFilter()];

      const queryTypeValue = query.queryType || "Persons";

      setQueryName(query.name || "");
      setQueryDescription(query.description || "");
      setVisibility(query.visibility || "Private");
      setQueryType(queryTypeValue);
      setSelectedColumns(selectedColumnsValue);
      setSortField(sortFieldValue);
      setSortDirection(sortDirectionValue);
      setFilters(filtersValue);

      const payload = buildPayloadFromValues({
        queryTypeValue,
        pageSizeValue: pageSize,
        includeTotalCountValue: includeTotalCount,
        selectedColumnsValue,
        sortFieldValue,
        sortDirectionValue,
        filtersValue,
      });

      const res = await previewQuery(domain, jwtToken, gUserId, payload);

      setRows(res?.rows || []);
      setTotalCount(res?.totalCount || 0);
    } catch (e) {
      console.error("Load saved query error", e);
    } finally {
      setLoading(false);
    }
  };

  const loadFields = async () => {
    try {
      setLoading(true);
      const data = await getFields(domain, jwtToken, gUserId, queryType);
      setFields(data || []);

      const defaultCols = (data || [])
        .filter((f) => f.defaultVisible)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((f) => f.fieldName);
      setSelectedColumns(defaultCols);
    } catch (e) {
      console.error("Error loading fields", e);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedQueries = async () => {
    try {
      setLoading(true);
      const data = await getSavedQueries(domain, jwtToken, gUserId);
      const filteredQueries = (data || []).filter((query) => {
        const owner = (query.ownerUserId || "").toLowerCase();
        const currentUser = (gUserId || "").toLowerCase();

        return query.visibility === "Global" || (query.visibility === "Private" && owner === currentUser);
      });
      // setSavedQueries(data || []);
      setSavedQueries(filteredQueries);
    } catch (e) {
      console.error("Error loading saved queries", e);
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => ({
    queryType,
    page: 1,
    pageSize,
    includeTotalCount,
    selectedColumns: getVisibleSelectedColumns(),
    sort: sortField ? [{ field: sortField, direction: sortDirection }] : [],
    filters: filters
      .filter((f) => f.field && f.operator && (f.value !== "" || f.operator === "isBlank"))
      .map((f, index) => ({
        logic: index === 0 ? "AND" : f.logic,
        field: f.field,
        operator: f.operator,
        value: f.value,
        value2: f.value2 || null,
      })),
    parameters: {},
  });

  const getExecuteSelectedColumns = () => {
    if (previewColumns && previewColumns.length > 0) {
      return getVisibleSelectedColumns(previewColumns.map((col) => col.dataField));
    }

    return getVisibleSelectedColumns(selectedColumns);
  };

  const buildExecutePayload = (page = 1, customPageSize = pageSize) => ({
    ...buildPayload(),
    selectedColumns: getExecuteSelectedColumns(),
    page,
    pageSize: customPageSize,
    includeTotalCount: true,
  });

  const onExecute = async () => {
    try {
      setLoading(true);

      const executePayload = buildExecutePayload(1, pageSize);

      const res = await executeQuery(domain, jwtToken, gUserId, executePayload);
      const result = res?.rows || res?.Rows || res?.data || res?.Data || [];
      const formattedResult = result.map((row) => {
        const newRow = { ...row };

        Object.keys(newRow).forEach((key) => {
          const isDateColumn = key.toLowerCase().includes("dob") || key.toLowerCase().includes("date");

          if (isDateColumn) {
            newRow[key] = formatDate(newRow[key]);
          }
        });

        return newRow;
      });

      const newWindow = window.open("", "_blank");

      if (!newWindow) {
        alert("Please allow popups for this site.");
        return;
      }
      const executeColumns = previewColumns && previewColumns.length > 0 ? previewColumns : queryColumns;
      const popupColumns = executeColumns.map((col) => {
        const actualField =
          result?.[0] && Object.keys(result[0]).find((key) => key.toLowerCase() === col.dataField.toLowerCase());

        return {
          ...col,
          dataField: actualField || col.dataField,
        };
      });

      newWindow.document.write(
        getExecutePopupHtml({
          result: formattedResult,
          popupColumns,
          queryType,
          totalCount: res?.totalCount || result.length,
          pageSize: res?.pageSize || pageSize,
          executePayload,
          apiUrl: `${process.env.REACT_APP_API_URL}/api/QueryBuilder/execute`,
          exportApiUrl: `${process.env.REACT_APP_API_URL}/api/QueryBuilder/export`,
          domain,
          jwtToken,
          gUserId,
        }),
      );

      newWindow.document.close();
    } catch (e) {
      console.error("Execute error", e);
    } finally {
      setLoading(false);
    }
  };

  const onPreview = async () => {
    try {
      setLoading(true);
      const res = await previewQuery(domain, jwtToken, gUserId, buildPayload());
      setRows(res?.rows || []);
      setTotalCount(res?.totalCount || 0);
    } catch (e) {
      console.error("Preview error", e);
    } finally {
      setLoading(false);
    }
  };

  const getExportFileConfig = (format) => {
    switch (format?.toUpperCase()) {
      case "PDF":
        return {
          fileName: "QueryResults.pdf",
          mimeType: "application/pdf",
        };

      case "EXCEL":
      case "XLSX":
        return {
          fileName: "QueryResults.xlsx",
          mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        };

      case "CSV":
      default:
        return {
          fileName: "QueryResults.csv",
          mimeType: "text/csv;charset=utf-8;",
        };
    }
  };
  const buildExportColumns = () =>
    selectedColumns.map((fieldName) => ({
      fieldName,
      friendlyLabel: getFriendlyColumnName(fieldName),
    }));

  const onExport = async () => {
    try {
      setLoading(true);

      const payload = {
        ...buildPayload(),
        exportFormat,
        columns: buildExportColumns(),
      };

      const res = await exportQuery(domain, jwtToken, gUserId, payload);

      const contentType = res.headers["content-type"] || "";

      // If backend returns JSON with base64FileContent
      if (contentType.includes("application/json")) {
        const text = await res.data.text();
        const json = JSON.parse(text);

        const byteCharacters = atob(json.base64FileContent);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], {
          type: json.contentType,
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = json.fileName || getExportFileConfig(exportFormat).fileName;

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        return;
      }

      // If backend returns direct file bytes
      const { fileName, mimeType } = getExportFileConfig(exportFormat);

      const blob = new Blob([res.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export error", e);
    } finally {
      setLoading(false);
    }
  };

  const onSave = async () => {
    const shares =
      visibility === "Group" && shareValue
        ? [
            {
              shareType,
              shareValue,
              canEdit: shareCanEdit,
              expirationDate: null,
            },
          ]
        : [];

    try {
      await saveQuery(domain, jwtToken, gUserId, {
        name: queryName,
        description: queryDescription,
        queryType,
        visibility,
        shares,
        filterJson: JSON.stringify(buildPayload().filters),
        sortJson: JSON.stringify(buildPayload().sort),
        selectedColumnsJson: JSON.stringify(selectedColumns),
        parametersJson: "{}",
        isActive: true,
        ownerUserId: gUserId,
      });
      await loadSavedQueries();
    } catch (e) {
      console.error("Save error", e);
    }
  };

  const queryColumns = useMemo(() => {
    const visibleColumns = getVisibleSelectedColumns(selectedColumns);

    if (!visibleColumns || visibleColumns.length === 0) return [];

    return visibleColumns.map((fieldName) => ({
      dataField: fieldName,
      caption: getFriendlyColumnName(fieldName),
    }));
  }, [selectedColumns, fields, queryType]);

  return (
    <div className="query-runner-container">
      <LoadPanel visible={loading} />

      <DynamicTabb />
      {/* <h2>Query Builder</h2> */}
      <div className="qb-main-layout">
        {/* LEFT */}
        <div className="qb-left">
          <QuerySetup
            queryTypes={queryTypes}
            queryType={queryType}
            setQueryType={setQueryType}
            fields={fields}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            sortField={sortField}
            setSortField={setSortField}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pageSize={pageSize}
            setPageSize={setPageSize}
            includeTotalCount={includeTotalCount}
            setIncludeTotalCount={setIncludeTotalCount}
          />

          <FilterBuilder filters={filters} setFilters={setFilters} fields={fields} />

          <SaveQueryPanel
            queryName={queryName}
            setQueryName={setQueryName}
            queryDescription={queryDescription}
            setQueryDescription={setQueryDescription}
            visibility={visibility}
            setVisibility={setVisibility}
            shareType={shareType}
            setShareType={setShareType}
            shareValue={shareValue}
            setShareValue={setShareValue}
            shareCanEdit={shareCanEdit}
            setShareCanEdit={setShareCanEdit}
            onSave={onSave}
            onRefreshSaved={loadSavedQueries}
          />
          <ActionPanel
            onPreview={onPreview}
            onExecute={onExecute}
            onExport={onExport}
            exportFormat={exportFormat}
            setExportFormat={setExportFormat}
          />
        </div>
        {/* RIGHT */}
        <div className="qb-right">
          <SavedQueriesGrid savedQueries={savedQueries} onLoadSavedQuery={loadSavedQuery} />

          <div className="qb-table-wrapper">
            <QueryResultsGrid rows={rows} columns={queryColumns} onColumnsChanged={setPreviewColumns} />
          </div>
        </div>
      </div>
    </div>
  );
}
